import useIsMobile from "@/lib/useIsMobile";
import { ListTodo, Database, HelpCircle, Hammer, ArrowRight } from "lucide-react";

// Items parked because they need a data source or a decision — kept here so
// they're never forgotten. Update the `have` line as data becomes available.
const NEEDS_DATA = [
  {
    title: "Catch / probability %",
    what: "Show your odds of getting your preferred performance (how full each perf is).",
    need: "Field size per performance — how many spots are taken vs available. Comes from the entry system's live counts.",
  },
  {
    title: "Pin number by your actual up-time",
    what: "Number map pins by the performance YOU are drawn up in, not just the rodeo's start date.",
    need: "Each rider's draw position/time per rodeo (from the posted draw once entries close).",
  },
  {
    title: "Qualification-rodeo filter",
    what: "Filter the map/calendar to Winter Series / finals-qualifying rodeos only.",
    need: "A qualification (and circuit) tag on each rodeo. Not currently in the schedule data.",
  },
  {
    title: '"How everyone\'s up" trade board',
    what: "See every rider's performance in a rodeo so you know exactly who to trade with.",
    need: "The full draw (all riders' positions) for the rodeo, not just your own.",
  },
  {
    title: "Entry fees in the Bank tab",
    what: "Net your winnings against what you paid in entry fees for a true profit number.",
    need: "Entry-fee amounts — pulled from your entry confirmation for each rodeo.",
  },
  {
    title: "Real Entered status from your login",
    what: 'Auto-flag "Entered" vs "Need to Enter" from your CPRA/PRCA account (right now you tap it yourself).',
    need: "A reliable per-rodeo entry feed from the association login sync.",
  },
  {
    title: "Rodeo Stock draw photos",
    what: "Show a picture of the bronc/bull/steer you've drawn.",
    need: "A photo source for stock. (Marked not a priority — coming later.)",
  },
];

const PLANNED = [
  {
    title: "Mobile My Rodeos card",
    what: "Bring the new My Rodeos layout (1st/2nd up-times, entry countdown, Entered toggle) to the mobile view.",
  },
  {
    title: 'Draw locked until entries close',
    what: 'Hard rule so the draw can never show before entries close (today it\'s hidden at the button, not enforced server-side).',
  },
  {
    title: "Voice: filter by circuit",
    what: 'Make "only show Canadian circuit rodeos" actually apply the map circuit filter by voice.',
  },
];

export default function Roadmap() {
  const m = useIsMobile();
  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const headBorder = m ? "border-white/10" : "border-[#E4E7EB]";

  return (
    <div className={page} data-testid="roadmap-page">
      <div className={`px-4 sm:px-6 py-5 border-b ${headBorder}`}>
        <div className="flex items-center gap-2">
          <ListTodo className="w-6 h-6 text-[#C8000F]" />
          <div>
            <h1 className={`font-display font-bold text-xl ${title}`}>Coming Soon</h1>
            <p className="text-xs text-slate-400">Ideas from testing that are parked until we have the data or a decision — so nothing gets forgotten.</p>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-5 max-w-3xl space-y-6">
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Database className="w-4 h-4 text-[#E68A2E]" />
            <h2 className={`font-display font-bold text-base ${title}`}>Needs a data source to unlock</h2>
          </div>
          <div className="space-y-2.5">
            {NEEDS_DATA.map((it, i) => (
              <div key={i} className={`rounded-xl border p-4 ${card}`} data-testid={`roadmap-data-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className={`font-display font-bold text-sm ${title}`}>{it.title}</div>
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md bg-[#E68A2E]/15 text-[#9E0008]">Needs data</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">{it.what}</p>
                <div className="mt-2 flex items-start gap-1.5 text-[12px] text-slate-500 bg-[#00000008] rounded-md px-2.5 py-1.5">
                  <HelpCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#E68A2E]" />
                  <span><b className="font-semibold">What we need:</b> {it.need}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-3">
            <Hammer className="w-4 h-4 text-[#3B6B1E]" />
            <h2 className={`font-display font-bold text-base ${title}`}>Planned build (no blocker)</h2>
          </div>
          <div className="space-y-2.5">
            {PLANNED.map((it, i) => (
              <div key={i} className={`rounded-xl border p-4 ${card}`} data-testid={`roadmap-planned-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className={`font-display font-bold text-sm ${title}`}>{it.title}</div>
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md bg-[#3B6B1E]/15 text-[#3B6B1E] flex items-center gap-1">Planned <ArrowRight className="w-3 h-3" /></span>
                </div>
                <p className="text-sm text-slate-500 mt-1">{it.what}</p>
              </div>
            ))}
          </div>
        </section>

        <p className="text-[11px] text-slate-400">Got access to any of the data above (entry counts, entry confirmations, full draws, circuit tags)? Tell the team and we'll wire it straight in.</p>
      </div>
    </div>
  );
}
